import { useMemo } from "react";

import { useAppSelector } from "app/hooks";
import {
  type PatientAlertPopupMode,
  isPatientAlertPopupDaily,
  isPatientAlertPopupOn,
  isPatientAlertPopupRequired,
  shouldShowPatientAlertPopup
} from "constants/patient-alerts";
import {
  selectCurrentPractice,
  selectCurrentUser
} from "features/auth/auth-slice";

export type PatientAlertPopupState = {
  /** The practice's mode; undefined when the backend predates the field (treated as off). */
  mode: PatientAlertPopupMode | undefined;
  /** Any of the three ON modes. */
  isOn: boolean;
  /** Either REQUIRED mode: the personal switch is locked. */
  isRequired: boolean;
  /** REQUIRED_DAILY: reviews reset on the first open of each practice-local day. */
  isDaily: boolean;
  /** The person's own opt-out preference (null/undefined = on). */
  showAlertPopupPreference: boolean | null | undefined;
  /** The final answer: does the pop-up show for the current user. */
  showPopupForMe: boolean;
};

/**
 * DEV-3110: the one place that combines the practice mode with the current user's own preference.
 * Every surface (pop-up, header pill, settings cards, report columns) reads this rather than the
 * raw practice field, so the opt-out rule lives in exactly one spot.
 */
export default function usePatientAlertPopupMode(): PatientAlertPopupState {
  const mode = useAppSelector(selectCurrentPractice)?.patient_alert_popup_mode;
  const showAlertPopupPreference =
    useAppSelector(selectCurrentUser)?.user_preferences?.patient_chart
      ?.show_alert_popup;

  return useMemo(
    () => ({
      mode,
      isOn: isPatientAlertPopupOn(mode),
      isRequired: isPatientAlertPopupRequired(mode),
      isDaily: isPatientAlertPopupDaily(mode),
      showAlertPopupPreference,
      showPopupForMe: shouldShowPatientAlertPopup(
        mode,
        showAlertPopupPreference
      )
    }),
    [mode, showAlertPopupPreference]
  );
}
